/**
 * Combat SFX. Real CC0 samples for the moments that matter (gunfire, metal
 * clash) + gritty noise-synth impacts for punches/hits. No musical tones — this
 * is a mafia street fight, not a platformer. Web Audio; respects a mute toggle.
 *
 * Samples: tommygun.ogg (PPSh burst) + clash.ogg (blade-on-blade) — CC0, from
 * PanderMusubi/sound-effects-library-weapons (Still North Media, public domain).
 */
import tommygunUrl from '../assets/omerta/sfx/tommygun.ogg';
import clashUrl from '../assets/omerta/sfx/clash.ogg';

let ctx: AudioContext | null = null;
let muted = false;
const buffers: Record<string, AudioBuffer | null> = {};

export function setMuted(m: boolean) { muted = m; }
export function isMuted() { return muted; }

function ac(): AudioContext | null {
  if (muted) return null;
  try {
    ctx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch { return null; }
}

// Lazy-load + decode a sample the first time it's needed.
function load(key: string, url: string) {
  const c = ac(); if (!c || buffers[key] !== undefined) return;
  buffers[key] = null; // in-flight marker
  fetch(url).then((r) => r.arrayBuffer()).then((b) => c.decodeAudioData(b))
    .then((buf) => { buffers[key] = buf; }).catch(() => { buffers[key] = null; });
}
// Preload on module import so the first shot isn't silent.
setTimeout(() => { load('gun', tommygunUrl); load('clash', clashUrl); }, 0);

/** Play a decoded sample, optionally only its first `slice` seconds, at `vol`. */
function play(key: string, url: string, vol = 0.8, slice?: number) {
  const c = ac(); if (!c) return;
  const buf = buffers[key];
  if (!buf) { load(key, url); return; } // not ready yet — skip (next time)
  const src = c.createBufferSource(); src.buffer = buf;
  const g = c.createGain(); g.gain.value = vol;
  src.connect(g).connect(c.destination);
  src.start(0);
  if (slice) src.stop(c.currentTime + slice);
}

// ── Real samples ────────────────────────────────────────────────────────────
/** Gunfire — a single crack (slice of the burst) or a full auto burst. */
export function gunshot(burst = false) { play('gun', tommygunUrl, burst ? 0.9 : 0.85, burst ? undefined : 0.16); }
/** Metallic parry / block. */
export function clash() { play('clash', clashUrl, 0.7, 0.4); }

// ── Gritty noise-synth impacts (no tones) ─────────────────────────────────────
function noise(c: AudioContext, dur: number): AudioBufferSourceNode {
  const n = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, n, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource(); src.buffer = buf; return src;
}

/** A heavy bare-knuckle thud — low filtered noise, sharp attack, fast decay. */
export function punch(big = false) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = noise(c, big ? 0.22 : 0.16);
  const lp = c.createBiquadFilter(); lp.type = 'lowpass';
  lp.frequency.setValueAtTime(big ? 900 : 650, t);
  lp.frequency.exponentialRampToValueAtTime(120, t + 0.12);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(big ? 0.9 : 0.6, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + (big ? 0.2 : 0.14));
  src.connect(lp).connect(g).connect(c.destination); src.start(t);
}

/** A dull body hit taken. */
export function hit() {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = noise(c, 0.12);
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 380;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.5, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
  src.connect(lp).connect(g).connect(c.destination); src.start(t);
}

/** A thrown-object whoosh (mage). */
export function whoosh() {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = noise(c, 0.3);
  const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(500, t); bp.frequency.exponentialRampToValueAtTime(2200, t + 0.22);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.35, t + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
  src.connect(bp).connect(g).connect(c.destination); src.start(t);
}

/** Soft restorative swell (patch-up) — filtered noise, no melody. */
export function heal() {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = noise(c, 0.4);
  const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 3; bp.frequency.value = 1200;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.18, t + 0.15);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
  src.connect(bp).connect(g).connect(c.destination); src.start(t);
}

/** End stings — a gunfire flourish on a win, a low boom on a loss. */
export function sting(win: boolean) {
  if (win) { gunshot(true); return; }
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = noise(c, 0.6);
  const lp = c.createBiquadFilter(); lp.type = 'lowpass';
  lp.frequency.setValueAtTime(300, t); lp.frequency.exponentialRampToValueAtTime(60, t + 0.5);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.7, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
  src.connect(lp).connect(g).connect(c.destination); src.start(t);
}

/** Route an attack by hero class + action to the right sound. */
export function attackSfx(cls: string | undefined, action: string) {
  const big = action === 'heavy' || action === 'ability';
  if (cls === 'ARCHER' || cls === 'PALADIN') gunshot(big);
  else if (cls === 'MAGE') whoosh();
  else punch(big);
}
